<template>
  <div :style="_styles">
    <c360-app-provider :theme-code="themeCode" :token="token" />

    <c360-alerts v-if="!hideAlerts" :url="path" />
    <c360-what-is-new v-if="showWhatIsNewDialog" />
    <c360-chatbot
      v-if="canSeeSupportChatBot"
      :token="token"
      :product-id="productId"
      :theme-code="themeCode"
      :url="path"
      :statistic-support="canUseStatisticChatBot"
      v-bind="isCookieBasedAuth ? {} : { 'refresh-token': tokenRefresh }"
    />
    <slot />
  </div>
</template>

<script>
  import { C360Alerts } from '../../../widgets/Alerts';
  import { C360WhatIsNew } from '../../../widgets/WhatIsNew';
  import { C360Ability } from '../../../shared/abilities';
  import { injectStyle, injectScript } from '../../../shared/utils/dom';

  /**
   * Provides $c360 functionality: theme, alerts, chatbot, components related business logic.
   */
  export default {
    name: 'C360Provider',

    props: {
      /**
       * Tells which version of web components to use.
       * If a specific version is passed, it will use that version.
       * Otherwise, it will use the 'current' version.
       * Script and styles will be injected at beforeMount hook in this component.
       */
      webComponentsVersion: {
        type: String,
        default: 'current',
      },
      /**
       * Specifies which environment to use for web components.
       * Allowed values: 'prod', 'dev', 'stg'.
       */
      useSpecificWebComponentsEnv: {
        type: String,
        default: 'prod',
        validator: (value) => ['prod', 'dev', 'stg'].includes(value),
      },
      /**
       * If passed applies height: 100% to wrapper element.
       */
      fullHeight: {
        type: Boolean,
        default: false,
      },

      /**
       * Current page fullPath.
       * Once it changed, it will cause Alerts component rerender.
       */
      path: {
        type: String,
        required: true,
      },

      /**
       * Ability object returned by defineAbilityFor function.
       * @type {import('@casl/ability').PureAbility}
       */
      ability: {
        type: Object,
        required: true,
      },

      /**
       * On which exact page show the dialog.
       */
      config: {
        type: Object,
        default: () => ({
          whatIsNewDialog: { page: '' },
        }),
      },

      /**
       * Current user accessToken.
       * Required to do redirects.
       */
      accessToken: {
        type: String,
        default: '',
      },

      /**
       * Current user refreshToken.
       * Required to do redirects.
       */
      refreshToken: {
        type: String,
        default: '',
      },

      /**
       * Function that returns the current user's token.
       * Required for redirects.
       */
      tokenProvider: {
        type: Function,
        default: () => () => '',
      },

      /**
       * Function that returns the current user's access token.
       * Required for ChatBot, Alerts and What's New Dialog.
       *
       * TODO: remove this prop once all products have migrated to cookie based auth
       */
      accessTokenProvider: {
        type: Function,
        default: () => () => '',
      },

      /**
       * Overrides $c360.product value provided for plugin (optional).
       */
      product: {
        type: String,
      },

      /**
       * Will hide alerts altogether if passed.
       */
      hideAlerts: {
        type: Boolean,
        default: false,
      },

      /**
       * Product Id of product that is currently being used for chat bot token refresh method.
       */
      productId: {
        type: [String, Number],
        required: true,
      },

      /**
       * Theme code of product that is currently being used for chat bot token refresh method.
       */
      themeCode: {
        type: [String, Number],
        required: true,
      },

      /**
       * Remove passing refresh token to chatbot.
       */
      isCookieBasedAuth: {
        type: Boolean,
        default: false,
      },
    },

    components: {
      C360Alerts,
      C360WhatIsNew,
    },

    /**
     * Injects $c360 related objects.
     */
    provide() {
      const $c360 = this.$c360;

      if (this.product) {
        $c360.product = this.product;
      }

      return {
        $c360,
        $can: (action, entity) => {
          return this.ability.can(action, entity);
        },
        $tokenProvider: () => this.refreshToken || this.tokenProvider(),
        $accessTokenProvider: () => this.token,
        $productId: () => this.productId,
        $themeCode: () => this.themeCode,
      };
    },

    beforeMount() {
      const env = this.useSpecificWebComponentsEnv;
      const baseUrl = `https://c360-wc-static-${env}.s3.amazonaws.com/versions/${this.webComponentsVersion}`;

      injectScript(true, `${baseUrl}/esm/c360.js`);
      injectStyle(`${baseUrl}/c360/c360.css`);
    },

    computed: {
      _styles() {
        const styles = [];

        if (this.fullHeight) {
          styles.push({ height: '100%' });
        }

        return styles;
      },

      showWhatIsNewDialog() {
        const { page = '' } = this.config.whatIsNewDialog;
        return this.path === page;
      },

      canSeeSupportChatBot() {
        return this.ability.can(C360Ability.Interact, 'chatbot');
      },

      canUseStatisticChatBot() {
        return this.ability.can(C360Ability.Interact, 'statistic-chatbot');
      },

      token() {
        return this.accessToken || this.accessTokenProvider();
      },
      tokenRefresh() {
        return this.refreshToken || this.tokenProvider();
      },
    },
  };
</script>
